import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Target, Users, Eye, Handshake, Play } from "lucide-react";
import { Button } from "../components/ui/button";
import { SectionHeading } from "../components/shared/SectionHeading";
import { siteContent, images } from "../data/content";

const iconMap = {
  Consistency: Target,
  Reliability: Users,
  Transparency: Eye,
  Partnership: Handshake,
};

export const About = () => {
  return (
    <div>
      {/* Hero */}
      <section className="py-16 md:py-24 bg-charcoal-paper border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
              Our Story
            </span>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-6"
              data-testid="about-title"
            >
              About Us
            </h1>
            <p className="text-lg text-zinc-400">
              Learn about our journey as an Indonesian coconut shell charcoal manufacturer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Video Section */}
      <section className="py-24 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            title="Company Profile"
            subtitle="Watch our video to learn more about Samudra Charcoal"
          />
          <div className="max-w-4xl mx-auto">
            <div className="aspect-video bg-charcoal-paper border border-white/5 overflow-hidden">
              <video
                controls
                className="w-full h-full object-cover"
                poster={images.charcoalGlow}
                data-testid="company-video"
              >
                <source src={images.companyVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-charcoal-paper">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
                {siteContent.company.legalName}
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-6">
                Manufacturing Excellence Since 2016
              </h2>
              <p className="text-zinc-300 mb-6 leading-relaxed">
                {siteContent.about.story}
              </p>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Our factory has been audited by Carsurin, and we pride ourselves on maintaining consistent quality across every shipment. 
                We work with importers, distributors, and hookah brands worldwide, offering both our standard product and complete private label services.
              </p>
              <div className="flex items-center gap-8">
                <div>
                  <div className="font-heading text-4xl text-coconut">2016</div>
                  <div className="text-zinc-500 text-sm uppercase">Established</div>
                </div>
                <div className="w-px h-12 bg-white/10" />
                <div>
                  <div className="font-heading text-4xl text-coconut">Global</div>
                  <div className="text-zinc-500 text-sm uppercase">Export Markets</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] bg-charcoal-subtle border border-white/5 overflow-hidden">
                <img
                  src={images.charcoalGlow}
                  alt="Samudra Charcoal products"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-coconut p-6">
                <div className="font-heading text-charcoal text-xl uppercase">Samudra Charcoal</div>
                <div className="text-charcoal/70 text-sm">Indonesia</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-charcoal-paper">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            title="Our Values"
            subtitle="The principles that guide how we work with our partners"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {siteContent.about.values.map((value, index) => {
              const Icon = iconMap[value.title];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-charcoal border border-white/5 p-8 text-center"
                >
                  {Icon && (
                    <div className="w-14 h-14 bg-charcoal-subtle border border-white/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-coconut" />
                    </div>
                  )}
                  <h4 className="font-heading text-white uppercase text-lg mb-2">{value.title}</h4>
                  <p className="text-zinc-500 text-sm">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-charcoal noise-overlay">
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="aspect-square bg-charcoal-paper border border-white/5 overflow-hidden col-span-2">
                <img src={images.charcoalGlow} alt="Glowing charcoal" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square bg-charcoal-paper border border-white/5 overflow-hidden col-span-2">
                <img src={images.logistics} alt="Container shipping" className="w-full h-full object-cover" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
                Why Partner With Us
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-6">
                Your Reliable Charcoal Supplier
              </h2>
              <ul className="space-y-4 mb-8">
                {[
                  "Direct factory pricing with no middlemen",
                  "Consistent quality across every shipment",
                  "Flexible MOQ for new partnerships",
                  "Complete private label services",
                  "Experienced export documentation support",
                  "Responsive communication in English",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-zinc-300">
                    <span className="w-2 h-2 bg-coconut" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <Button
                  asChild
                  className="bg-coconut text-charcoal hover:bg-coconut-hover font-heading uppercase"
                  data-testid="about-quote-btn"
                >
                  <Link to="/contact">
                    Get in Touch
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 font-heading uppercase"
                >
                  <a
                    href={`${siteContent.contact.whatsappLink}?text=${encodeURIComponent(siteContent.contact.whatsappMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    WhatsApp Us
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-coconut">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-charcoal mb-6">
            Let's Build a Partnership
          </h2>
          <p className="text-charcoal/80 mb-8 max-w-2xl mx-auto">
            Whether you need bulk supply or private label services, we're ready to discuss how we can support your business.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-charcoal text-white hover:bg-charcoal-paper font-heading uppercase"
          >
            <Link to="/contact">
              Contact Us Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};
